import { ClassNameOnlyProps } from 'module/types/common';
import { AiFieldFilter, FieldFilter } from 'module/types/filter-field/filter';
import { isAiFieldFilterCriteria } from 'module/types/filter-field/grouped-filter/utils';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { RTEContent } from 'module/ui/components/atoms/rich-text-editor-input/html-content';
import { Tag } from 'module/ui/components/atoms/tag';
import {
  getRandomColor,
  Short01,
  SingleLineEllipsis,
} from 'module/ui/components/styles';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FilterCriterionAsText } from '../as-text/criteria-as-text';
import { UserOptionsGetter } from '../types';

const MutlilineWithEllipsis = styled(RTEContent)<{ line?: number }>`
  .rte-editor {
    display: -webkit-box;
    -webkit-line-clamp: ${(props) => props.line || 1};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal !important;
  }
`;

export const AiField = styled(MutlilineWithEllipsis).attrs({
  line: 1,
})`
  ${Short01};
`;
export const BadgeWrapper = styled(CenterAlignedBox)`
  flex: 1;
  flex-wrap: wrap;
`;
export const TagWrapper = styled.div`
  .ant-tag {
    margin: 0;
    span {
      ${SingleLineEllipsis};
      max-width: 300px;
    }
  }
`;

const defaultMaxVisibleFilters = 3;

const AiGeneratedTag = styled(Tag)`
  height: var(--line-height-04);
`;

export interface AsBadgesBaseProps extends ClassNameOnlyProps {
  userOptionsGetter?: UserOptionsGetter;
  showMinimumBadges?: boolean;
  maximumVisibleFilters?: number;
  moreBadgesText?: (count: number) => React.ReactNode;
}
interface Props extends AsBadgesBaseProps {
  criteria: FieldFilter[];
}
export const AsBadgesBase: FC<Props> = ({
  criteria,
  showMinimumBadges,
  userOptionsGetter,
  className,
  maximumVisibleFilters = defaultMaxVisibleFilters,
  moreBadgesText = (count) => (
    <FormattedMessage
      id="plus_more"
      values={{
        count,
      }}
    />
  ),
}) => {
  const divRef = useRef({} as HTMLDivElement);
  const [hovered, setHovered] = useState(false);

  // Ref: https://github.com/facebook/react/issues/4492#issuecomment-569410248
  useEffect(() => {
    if (
      hovered &&
      divRef.current &&
      divRef.current?.matches(':hover') === false
    ) {
      setHovered(false);
    }
  }, [hovered]);
  const showFewerBadges =
    maximumVisibleFilters !== defaultMaxVisibleFilters || showMinimumBadges;

  const renderCriteria =
    showMinimumBadges || showFewerBadges
      ? criteria.slice(0, maximumVisibleFilters)
      : criteria;

  return (
    <BadgeWrapper
      ref={divRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
    >
      {renderCriteria.map((fieldFilter) => {
        const fieldId = isAiFieldFilterCriteria(fieldFilter)
          ? parseInt((fieldFilter as AiFieldFilter)?.id ?? '0')
          : fieldFilter.field.fieldId;
        const color = getRandomColor(Math.abs(fieldId));

        return (
          <TagWrapper key={fieldId}>
            {isAiFieldFilterCriteria(fieldFilter) ? (
              <AiGeneratedTag
                color={getRandomColor(
                  parseInt((fieldFilter as AiFieldFilter)?.id ?? '0'),
                )}
              >
                {' '}
                <FormattedMessage id="ai" />
                <AiField value={(fieldFilter as AiFieldFilter)?.value} />
              </AiGeneratedTag>
            ) : (
              <Tag color={color}>
                <FilterCriterionAsText
                  criterion={fieldFilter}
                  userOptionsGetter={userOptionsGetter}
                />
              </Tag>
            )}
          </TagWrapper>
        );
      })}
      {showFewerBadges && criteria.length > maximumVisibleFilters ? (
        <TagWrapper>
          <Tag color={'cool-gray'}>
            {moreBadgesText(criteria.length - maximumVisibleFilters)}
          </Tag>
        </TagWrapper>
      ) : null}
    </BadgeWrapper>
  );
};
